import React from 'react';
import { Bell, X } from 'lucide-react';
import { CityInput } from './CityInput';
import { City, RegistrationFormData } from '../types';
import { createRegistration } from '../services/registration';

interface NewAlertFormProps {
  onClose: () => void;
}

export function NewAlertForm({ onClose }: NewAlertFormProps) {
  const [form, setForm] = React.useState<RegistrationFormData>({
    originCity: '',
    destinationCities: [],
    maxPrice: undefined,
  });

  const [originCity, setOriginCity] = React.useState<City[]>([]);
  const [destinations, setDestinations] = React.useState<City[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [destinationError, setDestinationError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setDestinationError(null);

    // Validate origin city
    if (originCity.length === 0) {
      setError('Selecione uma cidade de origem');
      return;
    }

    // Validate at least one destination city
    if (destinations.length === 0) {
      setDestinationError('Adicione pelo menos uma cidade de destino');
      return;
    }

    setIsSubmitting(true);

    try {
      await createRegistration({
        ...form,
        originCity: originCity[0].name,
      });
      setSuccess(true);
      
      // Close modal and refresh page after a short delay
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao realizar cadastro';
      setError(errorMessage);
      
      // If it's a profile data error, show a more helpful message
      if (errorMessage.includes('Dados do usuário incompletos')) {
        setError('Dados do usuário incompletos. Por favor, atualize seu perfil com nome e WhatsApp antes de criar um alerta.');
      }
      
      console.error('Registration error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddOriginCity = (cityName: string) => {
    const newCity = { id: crypto.randomUUID(), name: cityName };
    setOriginCity([newCity]);
    setForm(prev => ({
      ...prev,
      originCity: cityName
    }));
  };

  const handleRemoveOriginCity = (cityId: string) => {
    setOriginCity([]);
    setForm(prev => ({
      ...prev,
      originCity: ''
    }));
  };

  const handleAddDestination = (cityName: string) => {
    setDestinationError(null);
    const newCity = { id: crypto.randomUUID(), name: cityName };
    setDestinations([...destinations, newCity]);
    setForm(prev => ({
      ...prev,
      destinationCities: [...prev.destinationCities, cityName]
    }));
  };

  const handleRemoveDestination = (cityId: string) => {
    const cityToRemove = destinations.find(city => city.id === cityId);
    if (cityToRemove) {
      setDestinations(destinations.filter(city => city.id !== cityId));
      setForm(prev => ({
        ...prev,
        destinationCities: prev.destinationCities.filter(city => city !== cityToRemove.name)
      }));
    }
  };

  return (
    <>
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Bell className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">
            Novo Alerta
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      <div className="p-6 overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-100 text-green-700 rounded-lg">
              Alerta cadastrado com sucesso!
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cidade de origem
            </label>
            <CityInput
              selectedCities={originCity}
              onAddCity={handleAddOriginCity}
              onRemoveCity={handleRemoveOriginCity}
              placeholder="Digite a cidade de origem"
              maxCities={1}
              isSingleSelect={true}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cidades de destino
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Você pode selecionar uma ou mais cidades para receber alertas
            </p>
            <CityInput
              selectedCities={destinations}
              onAddCity={handleAddDestination}
              onRemoveCity={handleRemoveDestination}
              placeholder="Digite uma cidade de destino"
              isSingleSelect={false}
            />
            {destinationError && (
              <p className="mt-1 text-sm text-red-600">{destinationError}</p>
            )}
          </div>

          <div>
            <label htmlFor="maxPrice" className="block text-sm font-medium text-gray-700 mb-1">
              Valor máximo que deseja pagar na passagem (opcional)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                R$
              </span>
              <input
                type="number"
                id="maxPrice"
                value={form.maxPrice || ''}
                onChange={(e) => setForm({ ...form, maxPrice: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Cadastrando...' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}